// Modal "como esse número foi calculado": a fórmula do KPI instanciada com os números
// reais da linha clicada (fechamento) — fórmula → substituição → resultado + contexto.
import {useId, useRef} from 'react'
import {X} from 'lucide-react'
import useDialog from './useDialog'
import {KpisJanelas} from '../types'
import {fmtMoney2, fmtNum, fmtPct, fmtPp} from '../utils/format'
import {AJUDA} from '../utils/ajudas'

export type TipoKpi = 'ns' | 'prazo' | 'corte'

interface Props {
  tipo: TipoKpi
  label: string // nome da linha (seller/indústria/BU/Total)
  kpis: KpisJanelas
  m1Label?: string
  onClose: () => void
}

const TITULOS: Record<TipoKpi, string> = {
  ns: 'NS Quantidade (Fill Rate)',
  prazo: '% no Prazo',
  corte: 'Corte',
}

function Linha({rotulo, children}: {rotulo: string; children: React.ReactNode}) {
  return (
    <div className='kpi-explanation-row'>
      <div className='kpi-explanation-label'>{rotulo}</div>
      <div className='kpi-explanation-value'>{children}</div>
    </div>
  )
}

export default function ExplicaKpi({tipo, label, kpis, m1Label, onClose}: Props) {
  const panel = useRef<HTMLDivElement>(null)
  const titleId = useId()
  useDialog(true, panel, onClose)

  const mtd = kpis.mtd
  const m1 = kpis.m1
  // payload antigo (carregado antes de uma atualização da API) não traz os somatórios crus
  const temNumeros = mtd.qtd_comprada !== undefined

  const deltaDe = (a: number | null | undefined, b: number | null | undefined) =>
    a !== null && a !== undefined && b !== null && b !== undefined ? a - b : null

  let formula: string
  let substituicao: React.ReactNode
  let contexto: React.ReactNode
  let delta: number | null
  let direcaoBoa: 'up' | 'down' = 'up'

  if (tipo === 'ns') {
    formula = 'NS = unidades entregues ÷ unidades compradas (só pedidos faturados)'
    substituicao = (
      <>
        {fmtNum(mtd.qtd_entregue)} ÷ {fmtNum(mtd.qtd_comprada)} ={' '}
        <strong>{fmtPct(mtd.ns_qtd)}</strong>
      </>
    )
    contexto = (
      <>
        {fmtPct(mtd.pct_apurado)} do volume do mês já faturado · {fmtNum(mtd.pedidos_em_aberto)}{' '}
        pedidos em aberto (sem faturamento nem corte confirmado) ficam fora do cálculo até o
        faturamento.
      </>
    )
    delta = deltaDe(mtd.ns_qtd, m1.ns_qtd)
  } else if (tipo === 'prazo') {
    formula = '% no prazo = pedidos enviados dentro do SLA ÷ pedidos recebidos'
    substituicao = (
      <>
        {fmtNum(mtd.pedidos_no_prazo)} ÷ {fmtNum(mtd.pedidos_considerados_prazo)} ={' '}
        <strong>{fmtPct(mtd.pct_no_prazo)}</strong>
      </>
    )
    contexto = (
      <>
        {fmtNum(mtd.pedidos_em_atraso)} pedidos em atraso (P1) no período — inclui os em aberto que
        já estouraram o SLA do seller × região (default de 3 dias corridos quando não configurado).
      </>
    )
    delta = deltaDe(mtd.pct_no_prazo, m1.pct_no_prazo)
  } else {
    formula = 'Corte = soma dos valores de corte dos itens; % de corte = corte ÷ valor comprado'
    substituicao = (
      <>
        {fmtMoney2(mtd.corte_valor)} ÷ {fmtMoney2(mtd.valor_comprado)} ={' '}
        <strong>{fmtPct(mtd.pct_corte)}</strong>
      </>
    )
    contexto = (
      <>
        O total de <strong>{fmtMoney2(mtd.corte_valor)}</strong> inclui rupturas. Descontos do
        seller e diferenças de arredondamento no faturamento não entram no corte.
      </>
    )
    delta = deltaDe(mtd.pct_corte, m1.pct_corte)
    direcaoBoa = 'down'
  }

  const deltaFavoravel = delta !== null && (direcaoBoa === 'up' ? delta > 0 : delta < 0)

  return (
    <div
      className='modal-overlay'
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        className='modal'
        ref={panel}
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className='modal-heading'>
          <h2 id={titleId}>
            {TITULOS[tipo]} — {label}
          </h2>
          <button
            type='button'
            className='icon-button'
            aria-label='Fechar explicação'
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>

        <Linha rotulo='Fórmula'>{formula}</Linha>
        {temNumeros ? (
          <>
            <Linha rotulo='Com os números do mês corrente (MTD)'>{substituicao}</Linha>
            <Linha rotulo='Contexto'>{contexto}</Linha>
          </>
        ) : (
          <Linha rotulo='Com os números do mês corrente (MTD)'>
            <span className='muted'>Recarregue a página para carregar os números detalhados.</span>
          </Linha>
        )}
        <Linha rotulo={`Comparação justa (${m1Label || 'M-1'})`}>
          {tipo === 'corte'
            ? fmtPct(m1.pct_corte)
            : fmtPct(tipo === 'ns' ? m1.ns_qtd : m1.pct_no_prazo)}{' '}
          no mês anterior até o mesmo dia
          {delta !== null && (
            <>
              {' '}
              →{' '}
              <span className={deltaFavoravel ? 'delta-good' : 'delta-bad'}>
                {delta === 0 ? '•' : delta > 0 ? '▲' : '▼'} {fmtPp(Math.abs(delta))}{' '}
                {delta === 0 ? '(sem mudança)' : deltaFavoravel ? '(melhorou)' : '(piorou)'}
              </span>
            </>
          )}
        </Linha>

        <p className='text-[11px] text-muted mt-3 leading-relaxed'>
          {AJUDA[tipo === 'ns' ? 'ns' : tipo === 'prazo' ? 'prazo' : 'corte']}
        </p>

        <div className='modal-actions'>
          <button className='btn secondary' onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}
